// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

// Maximum character limit for notes
const MAX_NOTE_LENGTH = 2000;

interface NoteDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;

  /**
   * Callback when the dialog is closed without submission
   */
  onClose: () => void;

  /**
   * Callback when a note is submitted
   */
  onSubmit: (content: string) => void;

  /**
   * Whether the submission is in progress
   */
  isSubmitting?: boolean;

  /**
   * The title to display in the dialog
   */
  title?: string;

  /**
   * Initial content for editing an existing note
   */
  initialContent?: string;

  /**
   * Button text for the submit button
   */
  submitButtonText?: string;
}

/**
 * A dialog component for adding or editing notes.
 *
 * This component handles the UI for note input and submission,
 * while delegating the actual submission logic to the parent component.
 * It includes a character counter and improved styling for better user experience.
 */
export function NoteDialog(props: NoteDialogProps) {
  const {
    isOpen,
    onClose,
    onSubmit,
    isSubmitting = false,
    title = 'Add note',
    initialContent = '',
    submitButtonText = 'Add',
  } = props;
  const [noteContent, setNoteContent] = useState(initialContent);
  const [isFocused, setIsFocused] = useState(false);

  // Calculate remaining characters
  const remainingChars = MAX_NOTE_LENGTH - noteContent.length;
  const isOverLimit = remainingChars < 0;

  // Update content when initialContent changes or dialog opens
  useEffect(() => {
    if (isOpen) {
      setNoteContent(initialContent);
      // Auto-focus will handle setting focus state
    }
  }, [initialContent, isOpen]);

  // Reset content when dialog closes
  const handleClose = () => {
    onClose();
  };

  // Handle submission
  const handleSubmit = () => {
    if (!noteContent.trim() || isOverLimit) return;
    onSubmit(noteContent);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={1}>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={8}
            placeholder="Write your note here..."
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            disabled={isSubmitting}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            error={isOverLimit}
            helperText={
              isOverLimit
                ? `Exceeded maximum length by ${Math.abs(remainingChars)} characters`
                : ''
            }
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                borderColor: isFocused ? 'primary.main' : 'divider',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.light',
                },
              },
            }}
          />

          {/* Character counter below the text field */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Notes support plain text only, including line breaks
            </Typography>
            <Typography
              variant="caption"
              color={
                isOverLimit
                  ? 'error'
                  : remainingChars < 100
                    ? 'warning.main'
                    : 'text.secondary'
              }
            >
              {remainingChars}/{MAX_NOTE_LENGTH}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting} size="medium">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!noteContent.trim() || isSubmitting || isOverLimit}
          variant="contained"
          color="primary"
          size="medium"
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting ? 'Saving...' : submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
